import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Button } from '@mui/material';
import { RecordatorioDetectado } from '../../types/ai';
import { Evento } from '../../types/email';
import aiEmailService from '../../services/aiEmailService';

interface RecordatoriosModalProps {
  isOpen: boolean;
  onClose: () => void;
  recordatorios: RecordatorioDetectado[];
  pacienteId: string;
  pacienteNombre: string;
  onRecordatoriosGuardados: () => void;
}

const RecordatoriosModal: React.FC<RecordatoriosModalProps> = ({
  isOpen,
  onClose,
  recordatorios,
  pacienteId,
  pacienteNombre,
  onRecordatoriosGuardados
}) => {
  const [recordatoriosEditables, setRecordatoriosEditables] = useState<RecordatorioDetectado[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [eventosDuplicados, setEventosDuplicados] = useState<any[]>([]);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

  // --- Toda tu lógica de estado y funciones permanece intacta ---
  // Función para manejar cambios en la recurrencia
  const handleRecurrenciaChange = (id: string, campo: string, valor: string) => {
    setRecordatoriosEditables(prev => 
      prev.map(r => 
        r.id === id ? { 
          ...r, 
          recurrencia_tipo: campo === 'tipo' ? valor : (r as any).recurrencia_tipo,
          recurrencia_intervalo: campo === 'intervalo' ? valor : (r as any).recurrencia_intervalo,
          editado: true 
        } : r
      )
    );
  };

  // Función para verificar duplicados en la base de datos
  const verificarDuplicadosEnBD = async () => {
    const recordatoriosSeleccionados = recordatoriosEditables.filter(r => r.seleccionado);
    
    if (recordatoriosSeleccionados.length === 0) return;

    setIsCheckingDuplicates(true);
    try {
      // Convertir recordatorios a formato de eventos para la verificación
      const eventosParaVerificar = recordatoriosSeleccionados.map(recordatorio => ({
        id: recordatorio.id,
        fecha: recordatorio.fecha,
        tipo: recordatorio.tipo,
        paciente_id: recordatorio.paciente_id,
        paciente_nombre: recordatorio.paciente_nombre,
        descripcion: recordatorio.descripcion,
        hora_inicio: recordatorio.hora_inicio,
        hora_fin: recordatorio.hora_fin,
        recurrencia_id: recordatorio.recurrencia_id
      }));

      const resultado = await aiEmailService.checkEventosDuplicates(eventosParaVerificar);
      setEventosDuplicados(resultado.eventos_duplicados || []);
    } catch (error) {
      console.error('Error al verificar duplicados:', error);
      setEventosDuplicados([]);
    } finally {
      setIsCheckingDuplicates(false);
    }
  };

  // Verificar duplicados cuando cambien los recordatorios seleccionados
  useEffect(() => {
    if (isOpen && recordatoriosEditables.length > 0) {
      verificarDuplicadosEnBD();
    }
  }, [isOpen, recordatoriosEditables]);

  // Función para verificar si hay eventos duplicados
  const verificarEventosDuplicados = (recordatorio: RecordatorioDetectado) => {
    // Verificar duplicados en el modal (mismo tipo, paciente y fecha)
    const eventosMismoTipo = recordatoriosEditables.filter(r => 
      r.id !== recordatorio.id && 
      r.seleccionado && 
      r.tipo === recordatorio.tipo && 
      r.paciente_id === recordatorio.paciente_id && 
      r.fecha === recordatorio.fecha
    );
    
    // Verificar duplicados en la base de datos
    const duplicadoEnBD = eventosDuplicados.find(d => 
      d.evento_nuevo.tipo === recordatorio.tipo &&
      d.evento_nuevo.paciente_nombre === recordatorio.paciente_nombre &&
      d.evento_nuevo.fecha === recordatorio.fecha
    );
    
    return eventosMismoTipo.length > 0 || !!duplicadoEnBD;
  };

  // Función para obtener información de eventos duplicados
  const obtenerEventosDuplicados = (recordatorio: RecordatorioDetectado) => {
    // Duplicados en el modal
    const duplicadosEnModal = recordatoriosEditables.filter(r => 
      r.id !== recordatorio.id && 
      r.seleccionado && 
      r.tipo === recordatorio.tipo && 
      r.paciente_id === recordatorio.paciente_id && 
      r.fecha === recordatorio.fecha
    );
    
    // Duplicados en la base de datos
    const duplicadoEnBD = eventosDuplicados.find(d => 
      d.evento_nuevo.tipo === recordatorio.tipo &&
      d.evento_nuevo.paciente_nombre === recordatorio.paciente_nombre &&
      d.evento_nuevo.fecha === recordatorio.fecha
    );
    
    return {
      enModal: duplicadosEnModal,
      enBD: duplicadoEnBD
    };
  };

  useEffect(() => {
    if (isOpen && recordatorios.length > 0) {
      // Inicializar recordatorios editables con IDs únicos y seleccionados por defecto
      const recordatoriosConIds = recordatorios.map((recordatorio, index) => ({
        ...recordatorio,
        id: recordatorio.id || `temp-${index}`,
        seleccionado: true,
        editado: false
      }));
      setRecordatoriosEditables(recordatoriosConIds);
    }
  }, [isOpen, recordatorios]);

  const handleToggleSeleccion = (id: string) => {
    setRecordatoriosEditables(prev => 
      prev.map(r => 
        r.id === id ? { ...r, seleccionado: !r.seleccionado } : r
      )
    );
  };

  const handleEditarRecordatorio = (id: string, campo: keyof RecordatorioDetectado, valor: string) => {
    setRecordatoriosEditables(prev => 
      prev.map(r => 
        r.id === id ? { ...r, [campo]: valor, editado: true } : r
      )
    );
  };

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case 'consulta': return 'Consulta';
      case 'recordatorio': return 'Recordatorio';
      case 'seguimiento': return 'Seguimiento';
      case 'plan alimentario': return 'Plan Alimentario';
      default: return tipo;
    }
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'consulta': return '#1976d2'; // azul
      case 'plan alimentario': return '#43a047'; // verde
      case 'recordatorio': return '#e53935'; // rojo
      case 'seguimiento': return '#f9a825'; // amarillo más oscuro para mejor legibilidad
      default: return '#757575'; // gris
    }
  };

  const getPrioridadColor = (prioridad: string) => {
    switch (prioridad) {
      case 'alta': return 'text-red-600';
      case 'media': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const handleGuardarRecordatorios = async () => {
    const recordatoriosSeleccionados = recordatoriosEditables.filter(r => r.seleccionado);
    
    if (recordatoriosSeleccionados.length === 0) {
      alert('Debes seleccionar al menos un evento para guardar.');
      return;
    }

    setIsLoading(true);
    try {
      // Convertir recordatorios a formato de eventos
      const eventos: Evento[] = recordatoriosSeleccionados.map(recordatorio => ({
        id: recordatorio.id,
        fecha: recordatorio.fecha,
        tipo: recordatorio.tipo,
        paciente_id: recordatorio.paciente_id,
        paciente_nombre: recordatorio.paciente_nombre,
        descripcion: recordatorio.descripcion,
        hora_inicio: recordatorio.hora_inicio,
        hora_fin: recordatorio.hora_fin,
        recurrencia_id: recordatorio.recurrencia_id
      }));

      await aiEmailService.createEventosBatch(eventos);
      onRecordatoriosGuardados();
      onClose();
    } catch (error) {
      console.error('Error al guardar recordatorios:', error);
      alert('Error al guardar los eventos. Por favor, intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // --- Fin de la lógica ---

  if (!isOpen) {
    return null;
  }

  return ReactDOM.createPortal(
    <div 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2100
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* 
        CAMBIO CLAVE: El contenedor del modal ahora es un flex-column.
        Se quita el padding y el overflow de aquí para controlar las secciones internas.
      */}
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '800px',
          height: '90vh', // Usamos height en lugar de maxHeight para que flexbox funcione correctamente
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
          position: 'relative',
          display: 'flex', // <-- CAMBIO CLAVE
          flexDirection: 'column', // <-- CAMBIO CLAVE
          overflow: 'hidden' // <-- CAMBIO CLAVE: para evitar que el contenedor principal tenga scroll
        }}
      >
        {/* === SECCIÓN HEADER (FIJA) === */}
        <div style={{ padding: '24px 24px 0 24px', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ fontSize: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                📅
              </div>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                Eventos Detectados
                <span style={{ backgroundColor: '#035fa4', color: 'white', padding: '4px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                  {recordatoriosEditables.filter(r => r.seleccionado).length} seleccionados
                </span>
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: 'pointer', padding: '0', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', transition: 'all 0.2s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#374151'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#6b7280'; }}
            >
              ×
            </button>
          </div>
        </div>

        {/* === SECCIÓN CONTENIDO (SCROLLEABLE) === */}
        {/* 
          CAMBIO CLAVE: Este nuevo div envuelve todo el contenido que debe tener scroll.
          flex: 1 hace que ocupe todo el espacio disponible.
          overflowY: 'auto' le da la barra de scroll vertical SOLO si es necesario.
        */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
          <div style={{ marginTop: '16px', fontSize: '14px', color: '#6b7280' }}>
            <p style={{ margin: 0 }}>
              La IA detectó los siguientes eventos en el mensaje del paciente. 
              Puedes editar, seleccionar o deseleccionar los que quieras guardar en tu agenda.
            </p>
            
            {/* Resumen de conflictos */}
            {(() => {
              const eventosConDuplicados = recordatoriosEditables.filter(r => r.seleccionado && verificarEventosDuplicados(r));
              if (eventosConDuplicados.length > 0) {
                return (
                  <div style={{ marginTop: '12px', padding: '8px 12px', backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', fontSize: '12px', color: '#92400e' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '14px' }}>⚠️</span>
                      <span style={{ fontWeight: '600' }}>Conflictos detectados:</span>
                      {isCheckingDuplicates && (<span style={{ fontSize: '10px', fontStyle: 'italic' }}> (Verificando...)</span>)}
                    </div>
                    <div style={{ fontSize: '11px', lineHeight: '1.4' }}>
                      Se encontraron {eventosConDuplicados.length} evento(s) que podrían ser duplicados (mismo tipo, paciente y fecha). Revisa los detalles abajo.
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          <div style={{ marginTop: '16px', marginBottom: '24px' }}>
            {recordatoriosEditables.map((recordatorio) => (
              // Tu JSX para cada recordatorio va aquí, sin cambios
              <div 
                key={recordatorio.id} 
                style={{
                  border: recordatorio.seleccionado ? `2px solid ${getTipoColor(recordatorio.tipo)}` : '1px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px',
                  backgroundColor: recordatorio.seleccionado ? `${getTipoColor(recordatorio.tipo)}08` : 'white',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <input
                    type="checkbox"
                    checked={recordatorio.seleccionado}
                    onChange={() => handleToggleSeleccion(recordatorio.id)}
                    style={{ 
                      marginTop: '4px',
                      width: '18px',
                      height: '18px',
                      accentColor: '#035fa4'
                    }}
                  />
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: getTipoColor(recordatorio.tipo),
                        flexShrink: 0
                      }} />
                      <span style={{
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: `${getTipoColor(recordatorio.tipo)}15`,
                        color: getTipoColor(recordatorio.tipo),
                        border: `1px solid ${getTipoColor(recordatorio.tipo)}30`
                      }}>
                        {getTipoLabel(recordatorio.tipo)}
                      </span>
                      
                      {/* Indicador de evento duplicado */}
                      {verificarEventosDuplicados(recordatorio) && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px',
                          backgroundColor: '#fef3c7',
                          border: '1px solid #f59e0b',
                          borderRadius: '12px',
                          fontSize: '11px',
                          color: '#92400e',
                          fontWeight: '500',
                          lineHeight: '1'
                        }}>
                          <span style={{ fontSize: '12px', lineHeight: '1', display: 'flex', alignItems: 'center' }}>⚠️</span>
                          <span style={{ lineHeight: '1' }}>Duplicado</span>
                        </div>
                      )}
                    </div>
  
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                          Descripción:
                        </label>
                        <textarea
                          value={recordatorio.descripcion || ''}
                          onChange={(e) => handleEditarRecordatorio(recordatorio.id, 'descripcion', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            minHeight: '60px',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => {
                            e.target.style.outline = 'none';
                            e.target.style.borderColor = getTipoColor(recordatorio.tipo);
                            e.target.style.boxShadow = `0 0 0 3px ${getTipoColor(recordatorio.tipo)}20`;
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = 'none';
                          }}
                          rows={3}
                          placeholder="Descripción del recordatorio..."
                        />
                      </div>
  
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                            Fecha:
                          </label>
                          <input
                            type="date"
                            value={recordatorio.fecha || ''}
                            onChange={(e) => handleEditarRecordatorio(recordatorio.id, 'fecha', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              transition: 'border-color 0.2s ease'
                            }}
                            onFocus={(e) => {
                              e.target.style.outline = 'none';
                              e.target.style.borderColor = getTipoColor(recordatorio.tipo);
                              e.target.style.boxShadow = `0 0 0 3px ${getTipoColor(recordatorio.tipo)}20`;
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#d1d5db';
                              e.target.style.boxShadow = 'none';
                            }}
                          />
                        </div>
  
                        <div>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                            Tipo:
                          </label>
                          <select
                            value={recordatorio.tipo || 'recordatorio'}
                            onChange={(e) => handleEditarRecordatorio(recordatorio.id, 'tipo', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              backgroundColor: 'white',
                              transition: 'border-color 0.2s ease'
                            }}
                            onFocus={(e) => {
                              e.target.style.outline = 'none';
                              e.target.style.borderColor = getTipoColor(recordatorio.tipo);
                              e.target.style.boxShadow = `0 0 0 3px ${getTipoColor(recordatorio.tipo)}20`;
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#d1d5db';
                              e.target.style.boxShadow = 'none';
                            }}
                          >
                            <option value="recordatorio">Recordatorio</option>
                            <option value="consulta">Consulta</option>
                            <option value="seguimiento">Seguimiento</option>
                            <option value="plan alimentario">Plan Alimentario</option>
                          </select>
                        </div>
                      </div>
  
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                            Hora Inicio:
                          </label>
                          <input
                            type="time"
                            value={recordatorio.hora_inicio || '09:00'}
                            onChange={(e) => handleEditarRecordatorio(recordatorio.id, 'hora_inicio', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              transition: 'border-color 0.2s ease'
                            }}
                          />
                        </div>
  
                        <div>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                            Hora Fin:
                          </label>
                          <input
                            type="time"
                            value={recordatorio.hora_fin || '10:00'}
                            onChange={(e) => handleEditarRecordatorio(recordatorio.id, 'hora_fin', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              transition: 'border-color 0.2s ease'
                            }}
                          />
                        </div>
                      </div>
  
                      {/* Campos de recurrencia */}
                      <div style={{ 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '8px', 
                        padding: '16px', 
                        backgroundColor: '#f9fafb',
                        marginTop: '8px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: getTipoColor(recordatorio.tipo)
                          }} />
                          <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                            🔄 Recurrencia (Opcional)
                          </label>
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#6b7280', marginBottom: '4px' }}>
                              Tipo:
                            </label>
                            <select
                              value={(recordatorio as any).recurrencia_tipo || 'ninguna'}
                              onChange={(e) => handleRecurrenciaChange(recordatorio.id, 'tipo', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontFamily: 'inherit',
                                backgroundColor: 'white'
                              }}
                            >
                              <option value="ninguna">Sin recurrencia</option>
                              <option value="diaria">Diaria</option>
                              <option value="semanal">Semanal</option>
                              <option value="mensual">Mensual</option>
                              <option value="anual">Anual</option>
                            </select>
                          </div>
  
                          <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#6b7280', marginBottom: '4px' }}>
                              Intervalo:
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="52"
                              value={(recordatorio as any).recurrencia_intervalo || '1'}
                              onChange={(e) => handleRecurrenciaChange(recordatorio.id, 'intervalo', e.target.value)}
                              disabled={(recordatorio as any).recurrencia_tipo === 'ninguna'}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontFamily: 'inherit',
                                backgroundColor: (recordatorio as any).recurrencia_tipo === 'ninguna' ? '#f3f4f6' : 'white',
                                color: (recordatorio as any).recurrencia_tipo === 'ninguna' ? '#9ca3af' : '#374151'
                              }}
                              placeholder="1"
                            />
                          </div>
                        </div>
                        
                        {(recordatorio as any).recurrencia_tipo && (recordatorio as any).recurrencia_tipo !== 'ninguna' && (
                          <div style={{ 
                            marginTop: '8px', 
                            fontSize: '12px', 
                            color: '#6b7280',
                            fontStyle: 'italic'
                          }}>
                            Este evento se repetirá cada {(recordatorio as any).recurrencia_intervalo || '1'} {
                              (recordatorio as any).recurrencia_tipo === 'diaria' ? 'día(s)' :
                              (recordatorio as any).recurrencia_tipo === 'semanal' ? 'semana(s)' :
                              (recordatorio as any).recurrencia_tipo === 'mensual' ? 'mes(es)' :
                              'año(s)'
                            }
                          </div>
                        )}
                      </div>
  
                      <div style={{ fontSize: '12px', color: '#6b7280', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Paciente: {recordatorio.paciente_nombre || 'No especificado'}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {recordatorio.editado && (
                            <span style={{ 
                              color: '#035fa4',
                              fontWeight: '600',
                              backgroundColor: '#035fa415',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '11px'
                            }}>
                              ✏️ Editado
                            </span>
                          )}
                        </div>
                      </div>
  
                      {/* Información de eventos duplicados */}
                      {verificarEventosDuplicados(recordatorio) && (
                        <div style={{
                          marginTop: '8px',
                          padding: '8px 12px',
                          backgroundColor: '#fef3c7',
                          border: '1px solid #f59e0b',
                          borderRadius: '8px',
                          fontSize: '12px',
                          color: '#92400e'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '14px' }}>⚠️</span>
                            <span style={{ fontWeight: '600' }}>Evento duplicado detectado:</span>
                          </div>
                          <div style={{ fontSize: '11px', lineHeight: '1.4' }}>
                            {(() => {
                              const duplicados = obtenerEventosDuplicados(recordatorio);
                              const totalDuplicados = duplicados.enModal.length + (duplicados.enBD ? duplicados.enBD.cantidad_existentes : 0);
                              const duplicadosEnModal = duplicados.enModal.length;
                              const duplicadosEnBD = duplicados.enBD ? duplicados.enBD.cantidad_existentes : 0;
                              
                              return (
                                <>
                                  Ya tienes {totalDuplicados} evento(s) del mismo tipo 
                                  ({getTipoLabel(recordatorio.tipo)}) para {recordatorio.paciente_nombre} el {recordatorio.fecha}.
                                  {duplicadosEnModal > 0 && duplicadosEnBD > 0 && (
                                    <span> ({duplicadosEnModal} en este modal, {duplicadosEnBD} en la agenda)</span>
                                  )}
                                  {duplicadosEnModal > 0 && duplicadosEnBD === 0 && (
                                    <span> ({duplicadosEnModal} en este modal)</span>
                                  )}
                                  {duplicadosEnModal === 0 && duplicadosEnBD > 0 && (
                                    <span> ({duplicadosEnBD} en la agenda)</span>
                                  )}
                                  <br />
                                  <span style={{ fontStyle: 'italic' }}>
                                    Puedes continuar guardando ambos eventos o deseleccionar uno de ellos.
                                  </span>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* === SECCIÓN FOOTER (FIJA) === */}
        <div style={{ padding: '16px 24px 24px 24px', flexShrink: 0, borderTop: '1px solid #e5e7eb', backgroundColor: '#f9fafb', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <Button
              onClick={onClose}
              disabled={isLoading}
              variant="outlined"
              color="inherit"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleGuardarRecordatorios}
              disabled={isLoading || recordatoriosEditables.filter(r => r.seleccionado).length === 0}
              variant="contained"
              color="primary"
            >
              {isLoading ? 'Guardando...' : `Guardar ${recordatoriosEditables.filter(r => r.seleccionado).length} eventos`}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RecordatoriosModal;